import React, { ReactElement } from 'react';
import { Box, Button, Typography, Link } from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {
  gray100,
  gray110,
  gray20,
  gray40,
  primaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { boldXxl, regularMd, regularSm } from '../../../constants/location';
import homeDocumentsImage from '../../assets/home_documents.png';

interface TutorialCardProps {
  onRegisterClick: () => void;
  onTutorialClick: () => void;
  onVideoClick: () => void;
}

const TutorialCard = ({
  onRegisterClick,
  onTutorialClick,
  onVideoClick,
}: TutorialCardProps): ReactElement => {
  const styles = {
    container: {
      width: '100%',
      boxSizing: 'border-box',
      backgroundColor: white,
      borderRadius: '8px',
      padding: '32px',
      border: `1px solid ${gray40}`,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '48px',
      '@media (max-width: 960px)': {
        flexDirection: 'column',
        gap: '24px',
      },
    },
    leftColumn: {
      flex: '0 0 auto',
      width: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '@media (max-width: 960px)': {
        width: '100%',
      },
    },
    rightColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
    },
    illustration: {
      width: '100%',
      maxWidth: '665px',
      height: 'auto',
      borderRadius: '8px',
      objectFit: 'contain',
    },
    title: {
      ...boldXxl,
      color: gray100,
      textAlign: 'center',
      '& strong': {
        color: primaryButtonBackgroundColor.backgroundColor,
      },
    },
    description: {
      ...regularMd,
      color: gray110,
      textAlign: 'center',
    },
    primaryButton: {
      ...primaryButtonBackgroundColor,
      height: '48px',
      padding: '0 32px',
      fontSize: '16px',
      fontWeight: 700,
      minWidth: '240px',
    },
    linksContainer: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    link: {
      ...regularSm,
      color: primaryButtonBackgroundColor.backgroundColor,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      '&:hover': {
        opacity: 0.8,
      },
    },
    divider: {
      width: '1px',
      height: '16px',
      backgroundColor: gray20,
    },
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftColumn}>
        <Box
          component="img"
          src={homeDocumentsImage}
          alt="書類仕分けのイメージ図"
          sx={styles.illustration}
        />
      </Box>

      <Box sx={styles.rightColumn}>
        <Typography sx={styles.title}>
          まずは<strong>読取パターン</strong>を登録しましょう
        </Typography>

        <Typography sx={styles.description}>
          書類の読み取り精度を高めるための初回設定です。
          <br />
          一度作れば、次回以降は自動で適用されます。
        </Typography>

        <Button sx={styles.primaryButton} onClick={onRegisterClick}>
          読取パターンを登録する
        </Button>

        <Box sx={styles.linksContainer}>
          <Link component="button" onClick={onTutorialClick} sx={styles.link}>
            <ReplayRoundedIcon sx={{ fontSize: '16px' }} />
            チュートリアルをもう一度みる
          </Link>
          <Box sx={styles.divider} />
          <Link component="button" onClick={onVideoClick} sx={styles.link}>
            <PlayArrowRoundedIcon sx={{ fontSize: '20px' }} />
            説明動画をみる
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default TutorialCard;
